class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.enableConversionLoading();
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class SubscribeUpgradeButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.handleClick.bind(this));
  }

  async handleClick(event) {
    event.preventDefault();

    const cartItems = this.closest('cart-drawer-items, cart-items');
    if (!cartItems) return;
    cartItems.enableConversionLoading();

    const { subscriptionId, quantitySelector, subVariant, index } = this.dataset;

    if (quantitySelector === 'true') {
      if (subVariant && subscriptionId) {
        const quantityInput = document.getElementById(`Drawer-quantity-${index}`) || document.getElementById(`Quantity-${index}`);
        const currentQuantity = quantityInput ? Number(quantityInput.value) : 1;
        
        try {
          // Pass skipSectionUpdate=true to avoid rendering empty cart during removal
          await cartItems.updateQuantity(index, 0, event, null, null, null, true);
          // Add subscription product with the current quantity (skip cart update)
          await cartItems.addSubscriptionProduct(subVariant, currentQuantity, subscriptionId, true);
          // Finally, update the cart UI once
          await cartItems.onCartUpdate();
        } catch (error) {
          console.error('Error during subscription upgrade:', error);
        } finally {
          cartItems.disableConversionLoading();  
        }
        return;
      }
    }

    const quantityInput = document.getElementById(`Drawer-quantity-${index}`) || document.getElementById(`Quantity-${index}`);
    
    if (quantityInput) {
      cartItems.updateQuantity(index, Number(quantityInput.value), event, null, null, subscriptionId);
    }
  }
}

customElements.define('subscribe-upgrade-button', SubscribeUpgradeButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  async validateQuantity(event) {
    const target = event.target;
    const { index } = target.dataset;
    const isSellingPlanSelect =  target.tagName === 'SELECT' && target.getAttribute('name') === 'update_selling_plan';

    const quantityData = isSellingPlanSelect 
      ? this.extractSellingPlanData(target, index)
      : this.extractQuantityInputData(target, index);

    if (!quantityData) return;

    const { quantityInput, inputValue, sellingPlan, shouldAddOneTime, otVariantId } = quantityData;
    this.enableConversionLoading();

    if (shouldAddOneTime) {
      try {
        // Remove the subscription product first (without UI update)
        await this.updateQuantity(
          index,
          0,
          event,
          document.activeElement?.getAttribute('name'),
          target.dataset.quantityVariantId,
          null,
          true 
        );
        // Then add the one-time product with the preserved quantity (skip cart update)
        await this.addSubscriptionProduct(otVariantId, inputValue, null, true);
        await this.onCartUpdate();
      } catch (error) {
        console.error('Error during product conversion:', error);
      } finally {
        this.disableConversionLoading();
      }
      return;
    }

    const max = parseInt(quantityInput.max);
    const step = parseInt(quantityInput.step);
    let message = '';

    if (inputValue > max) {
      message = window.quickOrderListStrings.max_error.replace('[max]', max);
    } else if (inputValue % step !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', step);
    }

    if (message) {
      this.setValidity(event, index, message);
      return;
    }

    quantityInput.setCustomValidity('');
    quantityInput.reportValidity();

    this.updateQuantity(
      index,
      inputValue,
      event,
      document.activeElement?.getAttribute('name'),
      target.dataset.quantityVariantId,
      sellingPlan
    );
  }

  extractSellingPlanData(target, index) {
    const quantityInput = document.getElementById(`Drawer-quantity-${index}`);
    if (!quantityInput) return null;

    const selectedOption = target.options[target.selectedIndex];
    const planQuantity = selectedOption?.dataset.quantity;
    const isQuantitySelector = target.dataset.quantitySelector === 'true';
    const sellingPlan = target.value && target.value !== '0' ? Number(target.value) : null;

    let inputValue;
    let shouldAddOneTime = false;
    let otVariantId = null;

    if (isQuantitySelector && planQuantity !== undefined) {
      const dataQuantity = Number(planQuantity);
      
      // If data-quantity is 0, it means switching to one-time - preserve current quantity
      if (dataQuantity === 0) {
        // Get current quantity from input instead of using 0
        inputValue = Number(quantityInput.value) || Number(quantityInput.getAttribute('value')) || 1;
        otVariantId = selectedOption?.dataset.oneTime;
        shouldAddOneTime = !!otVariantId;
      } else {
        // Use the plan's specified quantity
        inputValue = dataQuantity;
        quantityInput.value = inputValue;
      }
    } else {
      inputValue = Number(quantityInput.value);
    }

    return { quantityInput, inputValue, sellingPlan, shouldAddOneTime, otVariantId };
  }

  extractQuantityInputData(target, index) {
    const quantityInput = target;
    const inputValue = Number(target.value);
    // const sellingPlanSelect = this.querySelector(`select[name="update_selling_plan"][data-index="${index}"]`);

    let sellingPlan = null;

    // if (sellingPlanSelect?.dataset.quantitySelector === 'true') {
    //   const matchingOption = sellingPlanSelect.options[inputValue];
    //   sellingPlan = matchingOption 
    //     ? Number(matchingOption.value)
    //     : (sellingPlanSelect.value && sellingPlanSelect.value !== '0' 
    //         ? Number(sellingPlanSelect.value) 
    //         : null);
    // } else 
    if (target.dataset.sellingPlanId) {
      sellingPlan = Number(target.dataset.sellingPlanId);
    }

    return { quantityInput, inputValue, sellingPlan };
  }

  async addSubscriptionProduct(variantId, currentQuantity, sellingPlanId, skipCartUpdate = false) {
    if (!variantId) return;

    await fetch(routes.cart_add_url, {
      ...fetchConfig(),
      body: JSON.stringify({
        id: variantId,
        quantity: currentQuantity,
        selling_plan:sellingPlanId
      }),
    });

    if (!skipCartUpdate) {
      await this.onCartUpdate();
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const targetDrawerInner = document.querySelector('.drawer__inner');
          const sourceDrawerInner = html.querySelector('.drawer__inner');
          if (targetDrawerInner && sourceDrawerInner) {
            targetDrawerInner.replaceWith(sourceDrawerInner);
          }
          const cartDrawer = document.querySelector('cart-drawer');
          const sourceCartDrawer = html.querySelector('cart-drawer');
          if (cartDrawer && sourceCartDrawer) {
            cartDrawer.classList.toggle('is-empty', sourceCartDrawer.classList.contains('is-empty'));
          }
          window.updateCartComparePrices();
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          if (sourceQty) {
            this.innerHTML = sourceQty.innerHTML;
          }
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId, selling_plan, skipSectionUpdate = false) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      selling_plan,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: '/',
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        // If skipSectionUpdate is true, just return without updating UI or disabling loading
        // Loading will be disabled by the calling function after the full operation completes
        if (skipSectionUpdate) {
          return parsedState;
        }

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            // console.log("errors",errors);
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);
          window.updateCartComparePrices();

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch((error) => {
        // console.log("catch error",error);
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
        if(!skipSectionUpdate){
          this.disableConversionLoading();
        }
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
  
  enableConversionLoading() {
    const isCartDrawer = this.tagName === 'CART-DRAWER-ITEMS';
    
    if (isCartDrawer) {
      const cartDrawerContents = document.querySelector('#CartDrawer .cart-drawer-contents');
      const cartDrawerLoader = document.querySelector('#CartDrawer .cart-drawer-loader');
      
      if (cartDrawerContents) {
        cartDrawerContents.classList.add('cart__items--disabled');
        cartDrawerContents.style.opacity = '0.7';
      }
      
      if (cartDrawerLoader) {
        cartDrawerLoader.classList.remove('hidden');
      }
    }
  }

  disableConversionLoading() {
    const isCartDrawer = this.tagName === 'CART-DRAWER-ITEMS';
    
    if (isCartDrawer) {
      const cartDrawerContents = document.querySelector('#CartDrawer .cart-drawer-contents');
      const cartDrawerLoader = document.querySelector('#CartDrawer .cart-drawer-loader');
      
      if (cartDrawerContents) {
        cartDrawerContents.style.opacity = '';
        cartDrawerContents.classList.remove('cart__items--disabled');
      }
      
      if (cartDrawerLoader) {
        cartDrawerLoader.classList.add('hidden');
      }
    }
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
